import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Typography,
  Box,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { makeStyles } from '@mui/styles';
import HTTP from 'services/http';

const useStyles = makeStyles((theme: any) => ({
  dialogPaper: {
    minHeight: '80vh',
    maxHeight: '90vh',
    minWidth: '70vw',
    maxWidth: '90vw',
  },
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingRight: theme.spacing(1),
  },
  dialogContent: {
    padding: 0,
    paddingTop: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
  },
  pdfContainer: {
    width: '100%',
    height: '100%',
    minHeight: '70vh',
    border: 'none',
    pointerEvents: 'auto',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  errorContainer: {
    padding: theme.spacing(3),
    textAlign: 'center',
  },
  metadata: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: theme.spacing(2),
    padding: theme.spacing(1, 2),
    backgroundColor: theme.palette.grey[50],
    color: theme.palette.common.black,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  metadataItem: {
    display: 'flex',
    gap: theme.spacing(0.5),
    alignItems: 'center',
  },
  metadataLabel: {
    fontWeight: 600,
    fontSize: '0.85rem',
    color: theme.palette.common.black,
  },
  metadataValue: {
    color: theme.palette.common.black,
  },
}));

interface IOrderFormPreviewModalProps {
  open: boolean;
  onClose: () => void;
  documentId: number;
  patientId: number;
  customerId: number;
  metadata?: {
    fileName?: string;
    completedDate?: string;
    fillCycleNumber?: number;
    orderId?: number;
  };
}

export const OrderFormPreviewModal: React.FC<IOrderFormPreviewModalProps> = ({
  open,
  onClose,
  documentId,
  patientId,
  customerId,
  metadata,
}) => {
  const classes = useStyles();
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!documentId) {
      return;
    }

    let blobUrl: string | null = null;

    const fetchDocumentUrl = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await HTTP.get(
          `/documents/customers/${customerId}/patients/${patientId}/${documentId}`,
        );

        if (response.data?.url) {
          const pdfResponse = await fetch(response.data.url);
          const blob = await pdfResponse.blob();
          blobUrl = URL.createObjectURL(blob);
          setDocumentUrl(blobUrl);
        } else {
          setError('Document URL not found');
        }
      } catch (err: any) {
        console.error('Error fetching document:', err);
        setError(err?.message || 'Failed to load document');
      } finally {
        setLoading(false);
      }
    };

    fetchDocumentUrl();

    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [documentId, patientId, customerId]);

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      classes={{ paper: classes.dialogPaper }}
      aria-labelledby="order-form-preview-title"
    >
      <DialogTitle id="order-form-preview-title" className={classes.dialogTitle}>
        <Typography variant="h6">Last Order Form Preview</Typography>
        <IconButton onClick={handleClose} size="small" aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {metadata && (
        <Box className={classes.metadata}>
          {metadata.fileName && (
            <Box className={classes.metadataItem}>
              <Typography className={classes.metadataLabel}>File:</Typography>
              <Typography className={classes.metadataValue} variant="body2">
                {metadata.fileName}
              </Typography>
            </Box>
          )}
          {metadata.completedDate && (
            <Box className={classes.metadataItem}>
              <Typography className={classes.metadataLabel}>Completed:</Typography>
              <Typography className={classes.metadataValue} variant="body2">
                {formatDate(metadata.completedDate)}
              </Typography>
            </Box>
          )}
          {metadata.fillCycleNumber && (
            <Box className={classes.metadataItem}>
              <Typography className={classes.metadataLabel}>FC:</Typography>
              <Typography className={classes.metadataValue} variant="body2">
                #{metadata.fillCycleNumber}
              </Typography>
            </Box>
          )}
          {metadata.orderId && (
            <Box className={classes.metadataItem}>
              <Typography className={classes.metadataLabel}>Order:</Typography>
              <Typography className={classes.metadataValue} variant="body2">
                {metadata.orderId}
              </Typography>
            </Box>
          )}
        </Box>
      )}

      <DialogContent className={classes.dialogContent}>
        {loading && (
          <Box className={classes.loadingContainer}>
            <CircularProgress />
            <Typography>Loading order form...</Typography>
          </Box>
        )}

        {error && (
          <Box className={classes.errorContainer}>
            <Typography color="error" variant="h6" gutterBottom>
              Error Loading Document
            </Typography>
            <Typography color="textSecondary">{error}</Typography>
          </Box>
        )}

        {!loading && !error && documentUrl && (
          <iframe
            src={`${documentUrl}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
            className={classes.pdfContainer}
            title="Order Form Preview"
          />
        )}

        {!loading && !error && !documentUrl && (
          <Box className={classes.errorContainer}>
            <Typography color="textSecondary">No document available</Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
